import React, { useState, useEffect } from 'react';
import { teamMemberService } from '../services/teamMemberService';
import { partnerService } from '../services/partnerService';
import { imageService } from '../services/imageService';
import { TeamMember, Partner, Image } from '../types';

const DataDebug: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);
        
        const [membersData, partnersData, imagesData] = await Promise.all([
          teamMemberService.getActiveTeamMembers(),
          partnerService.getActivePartners(),
          imageService.getAllImages()
        ]);

        setTeamMembers(membersData);
        setPartners(partnersData);
        setImages(imagesData);

        console.log('=== DATA DEBUG RESULTS ===');
        console.log('Team Members:', membersData);
        console.log('Partners:', partnersData);
        console.log('Images:', imagesData);
        console.log('========================');
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading debug data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8 text-center">Data Debug Page</h1>
        
        {/* Team Members */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Team Members ({teamMembers.length})</h2>
          {teamMembers.length > 0 ? (
            <div className="space-y-4">
              {teamMembers.map((member) => (
                <div key={member.id} className="border rounded p-4">
                  <div className="flex items-center space-x-4">
                    <img 
                      src={member.imageUrl || 'https://placehold.co/100'} 
                      alt={member.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-bold">{member.name}</h3>
                      <p className="text-gray-600">{member.role}</p>
                      <p className="text-sm text-gray-500">Founder: {member.isFounder ? 'Yes' : 'No'}</p>
                      <p className="text-sm text-gray-500">Active: {member.active ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No team members found</p>
          )}
        </div>

        {/* Partners */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Partners ({partners.length})</h2>
          {partners.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.map((partner) => (
                <div key={partner.id} className="border rounded p-4">
                  <h3 className="font-bold">{partner.name}</h3>
                  <p className="text-gray-600 capitalize">{partner.type}</p>
                  <p className="text-sm text-gray-500">{partner.location}</p>
                  <p className="text-sm text-gray-500">Active: {partner.active ? 'Yes' : 'No'}</p>
                  <p className="text-sm text-gray-500">Featured: {partner.featured ? 'Yes' : 'No'}</p>
                  {partner.website && (
                    <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Website
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No partners found</p>
          )}
        </div>

        {/* Images */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold mb-4">Images ({images.length})</h2>
          {images.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <div key={image.id} className="border rounded p-4">
                  <img 
                    src={image.url} 
                    alt={image.altText || image.name}
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                  <h3 className="font-bold">{image.name}</h3>
                  <p className="text-gray-600 capitalize">{image.category}</p>
                  <p className="text-sm text-gray-500">Active: {image.active ? 'Yes' : 'No'}</p>
                  <p className="text-sm text-gray-500">Featured: {image.featured ? 'Yes' : 'No'}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No images found</p>
          )}
        </div>

        <div className="mt-8 text-center">
          <a 
            href="/about" 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to About Page
          </a>
        </div>
      </div>
    </div>
  );
};

export default DataDebug;
